import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useStripe } from '../../hooks/useStripe';
import { isProCheckoutPlan, PRO_MONTHLY_PRICE, PRO_PLAN_CHOICES, PRO_PRICE_LINE } from '../../lib/proPlans';

export default function ProPaywall() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscribeToPlan, createPortalSession, loading } = useStripe();
  const [plans, setPlans] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const checkout = (plan: 'monthly' | 'yearly') => {
    if (!user) {
      navigate('/auth');
      return;
    }
    void subscribeToPlan(plan);
  };

  const restore = () => {
    if (!user) {
      navigate('/auth?mode=login');
      return;
    }
    setNote('Restore opens the billing page for this account.');
    void createPortalSession();
  };

  return (
    <div className="font-myanmar fixed inset-0 z-40 overflow-y-auto bg-stone-50" data-testid="pro-paywall">
      <div className="mx-auto flex min-h-full w-full max-w-lg flex-col px-4 py-6">
        <button type="button" aria-label="Close" className="flex h-11 w-11 items-center justify-center text-2xl" onClick={() => navigate(-1)}>
          ×
        </button>
        <h1 className="mt-4 text-3xl font-semibold text-gray-900">Pro</h1>
        <p className="mt-6 text-3xl font-semibold text-gray-900" data-testid="pro-price">{PRO_PRICE_LINE}</p>
        <p className="lesson-subtext mt-1 text-sm text-gray-600">တစ်လ {PRO_MONTHLY_PRICE}</p>
        <p className="mt-3 text-sm leading-snug text-gray-800">ဖျက်ရလွယ်ပါတယ်။ ပေးပြီးသား ကာလကုန်မှ ရပ်ပါတယ်။</p>
        <p className="lesson-subtext mt-1 text-sm text-gray-600" data-testid="pro-cancel">Easy to cancel. Access lasts until the time you already paid for ends.</p>
        <p className="mt-3 text-sm text-gray-700">အခမဲ့သင်ခန်းစာတွေက Pro မလိုပါ။ Free lessons stay available.</p>
        <button
          type="button"
          disabled={loading}
          className="mt-6 flex min-h-[52px] w-full items-center justify-center rounded-2xl bg-teal-700 text-base font-semibold text-white disabled:opacity-60"
          data-testid="pro-continue"
          onClick={() => checkout('monthly')}
        >
          Continue
        </button>
        <button
          type="button"
          className="mt-3 min-h-[44px] text-sm font-semibold text-teal-800"
          data-testid="pro-view-plans"
          aria-expanded={plans}
          onClick={() => setPlans((open) => !open)}
        >
          View all plans
        </button>
        {plans ? (
          <ul className="mt-2 space-y-2" data-testid="pro-plan-list">
            {PRO_PLAN_CHOICES.map((plan) => (
              <li key={plan.id}>
                {isProCheckoutPlan(plan.id) ? (
                  <button
                    type="button"
                    className="flex min-h-[52px] w-full items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 text-left"
                    onClick={() => checkout(plan.id)}
                  >
                    <span>
                      <span className="block text-base font-semibold text-gray-900">{plan.line}</span>
                      <span className="block text-sm text-gray-600">{plan.detail}</span>
                    </span>
                  </button>
                ) : (
                  <div className="flex min-h-[52px] w-full flex-col justify-center rounded-2xl border border-gray-200 bg-white px-4 text-left" data-testid="driving-pass-price">
                    <span className="block text-base font-semibold text-gray-900">{plan.line}</span>
                    <span className="block text-sm text-gray-600">{plan.detail}</span>
                    <span className="block text-sm text-gray-600">တစ်ကြိမ်ပေးချေမှု</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : null}
        <button type="button" className="mt-4 min-h-[44px] text-sm font-semibold text-gray-800" data-testid="pro-restore" onClick={restore}>
          Restore purchase
        </button>
        {note ? <p className="text-sm text-gray-600">{note}</p> : null}
        <p className="mt-auto pt-8 text-sm text-gray-600">
          <Link to="/terms" className="underline" data-testid="pro-terms">Terms</Link>
          <span className="px-2">·</span>
          <Link to="/privacy" className="underline" data-testid="pro-privacy">Privacy</Link>
        </p>
      </div>
    </div>
  );
}
